import * as R from "ramda";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { addAlert } from "@src/store/app/app-actions";
import { stopEditingCartItem, updateCartItem } from "@store/cart/cart-actions";
import { AppState } from "@store/reducers";

import { CartItem, NewCartItem } from "@src/definitions/cart";
import { WishListItem } from "@src/definitions/wish-list";
import { useCartItemEditor } from "@src/utils/cart-utils";

import {
    getBookingPeriodOptions,
    getCartItemVersion,
    getVenueOptions,
    getVersionOptions
} from "@src/app/films/film-utils";

import i18next from "i18next";
import "./cart-item-editor.scss";

type Item = CartItem | WishListItem;

interface Props {
    cartItem: Item;
    cinemas: AppState["cinemas"]["data"];
    isInCart: boolean;
    autoUpdate?: boolean;
    updateItem: typeof updateCartItem;
    stopEditing?: typeof stopEditingCartItem;
    addAlert: typeof addAlert;
}

const CartItemEditor = (props: Props) => {
    const autoSave = (updatedItem: NewCartItem) => props.autoUpdate && saveChanges(updatedItem);
    const status = "status" in props.cartItem ? props.cartItem.status : "pending";
    const [lastSavedVersion, setLastSavedVersion] = useState<CartItem>({ ...props.cartItem, status });

    const {
        cartItem,
        updateCartItem,
        fixedPeriodDuration
    } = useCartItemEditor(lastSavedVersion, props.cinemas, autoSave);

    const { versionId, versions, bookingPeriodType, filmType } = cartItem;

    const _id = props.cartItem._id;
    const isFilm = (filmType === "film");
    const isDcp = (isFilm && cartItem.encoreType === "dcp");
    const isEncore = (filmType === "encore");
    const isMediabox = (isEncore && cartItem.encoreType === "mediabox");
    const version = getCartItemVersion(versions, versionId);
    const filmdepotEnabled = version?.hasFilmdepotMaterials || false;
    const fixedPeriodSelected = cartItem.bookingPeriodType !== "day";

    const versionOptions = getVersionOptions(versions);
    const bookingPeriodOptions = getBookingPeriodOptions(version);
    const venueOptions = getVenueOptions(props.cinemas, versions, versionId, bookingPeriodType);

    const saveChanges = async (updatedVersion: NewCartItem) => {
        try {
            await props.updateItem({ _id, ...updatedVersion });
            setLastSavedVersion({ _id, ...updatedVersion });
        } catch (e) {
            updateCartItem(lastSavedVersion, true);
            props.addAlert({
                type: "error",
                title: "Could not update cart item",
                message: e.message
            });
        }
    };

    useEffect(() => {
        const localVersion = { ...props.cartItem, ...cartItem };
        const incomingVersion = { ...cartItem, ...props.cartItem };

        if (!R.equals(localVersion, incomingVersion)) {
            updateCartItem(incomingVersion, true);
            setLastSavedVersion(incomingVersion);
        }
    }, [props.cartItem]);

    const versionLabel = versionOptions.find(option => option.value === versionId)?.label || "Unknown Version";

    return (
        <div className="CartItemEditor">
            <table>
                <tbody>
                    {isFilm && (
                        <tr>
                            <td className="CartItemEditor__label">{i18next.t("orders::Version")}</td>
                            <td className="CartItemEditor__value">{versionLabel}</td>
                        </tr>
                    )}
                    {!!bookingPeriodOptions.length && (isDcp || isFilm) && (
                        <tr>
                            <td className="CartItemEditor__label">{i18next.t("orders::Booking period")}</td>
                            <td className="CartItemEditor__value">{cartItem.bookingPeriodType ? cartItem.bookingPeriodType : "day"}</td>
                        </tr>
                    )}
                    {fixedPeriodSelected && (isDcp || isFilm) && (
                        <tr>
                            <td className="CartItemEditor__label">{i18next.t("orders::Fixed period duration")}</td>
                            <td className="CartItemEditor__value">{String(fixedPeriodDuration)}</td>
                        </tr>
                    )}
                    <tr>
                        <td className="CartItemEditor__label">{i18next.t("orders::Venues")}</td>
                        <td className="CartItemEditor__value">
                            <div className="CartItemEditor__venues">
                                {venueOptions.map(venue => (
                                    <div className="CartItemEditor__venueItem" key={venue.value}>
                                        {venue.label}
                                    </div>
                                ))}
                            </div>
                        </td>
                    </tr>
                    {(isDcp || isFilm) && (
                        <tr>
                            <td className="CartItemEditor__label">{i18next.t("orders::Date range")}</td>
                            <td className="CartItemEditor__value">{new Date(cartItem.startDate).toLocaleDateString()} - {new Date(cartItem.endDate).toLocaleDateString()}</td>
                        </tr>
                    )}
                    {(isMediabox && isEncore) && (
                        <tr>
                            <td className="CartItemEditor__label">{i18next.t("orders::Encore date")}</td>
                            <td className="CartItemEditor__value">{cartItem.encoreDate ? new Date(cartItem.encoreDate).toLocaleString() : ""}</td>
                        </tr>
                    )}
                    {filmdepotEnabled && (isDcp || isFilm) && (
                        <tr>
                            <td className="CartItemEditor__label">{i18next.t("orders::Include Filmdepot Materials")}</td>
                            <td className="CartItemEditor__value">
                                <input type="checkbox" checked={cartItem.includeFilmdepotMaterials} readOnly />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export const mapStateToProps = (state: AppState) => ({
    cinemas: state.cinemas.data
});

export default connect(mapStateToProps, { addAlert })(CartItemEditor);
